/**
 * +prof: the gathering HUD (docs/design/professions.md §6), registered in hud/widgets.ts after the
 * field widgets. All of it reads world/profView.ts (written by world/worldApp.ts) and hides outside a
 * world session.
 *
 *   profPanel     screen         the Professions panel (L): both skills with bars, tiers in their
 *                                skill-up colours, the next unlock, tracking toggles, the Auto setting
 *   profTooltip   screen         node tooltip under the cursor ("Requires Foraging 35", yields, state)
 *   profChannel   screen         the gather channel bar and the skill-up / tip toasts
 *   profMarkers   screen         tracked nodes: a diamond over each nearby one, edge arrows off screen
 *   profAuto      bottomCenter   the Hunt / Gather / Both chip on the AUTO button
 */
import type { GatherProfession } from '@keystone/sim';
import { MOSSVALE_NODES, PROFESSION_NAME } from '@keystone/content';
import type { HudWidget, HudWidgetDef } from '../widgets';
import { el, setText } from '../dom';
import { AUTO_MODES, AUTO_MODE_LABEL, profView } from '../../world/profView';
import { nodeTooltip, profRows } from './profModel';
import './professions.css';

const NODE_BY_ID = new Map(MOSSVALE_NODES.map((n) => [n.id, n]));
const PROF_ICON: Readonly<Record<GatherProfession, string>> = { foraging: '❦', mining: '⛏' };
const PROF_TINT: Readonly<Record<GatherProfession, string>> = { foraging: '#8fe07a', mining: '#e0b070' };

// ---------------------------------------------------------------------------

class ProfPanel implements HudWidget {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private v = -1;
  private open: boolean | null = null;

  constructor(host: HTMLElement) {
    this.root = el('div', 'kp-panel hk-hit hk-hidden', host);
    const head = el('div', 'kp-head', this.root);
    el('span', 'kp-title', head, 'Professions');
    el('span', 'kp-key', head, 'L');
    const x = el('button', 'kp-close', head, '×');
    x.type = 'button';
    x.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      profView.actions?.togglePanel();
    });
    this.body = el('div', 'kp-body', this.root);
  }

  update(): void {
    const v = profView;
    const open = v.active && v.panelOpen;
    if (open !== this.open) {
      this.open = open;
      this.root.classList.toggle('hk-hidden', !open);
      this.v = -1;
    }
    if (!open) return;
    const ver = v.skillsVersion * 1000 + v.settingsVersion;
    if (ver === this.v) return;
    this.v = ver;
    this.render();
  }

  private render(): void {
    const v = profView;
    this.body.replaceChildren();
    for (const r of profRows(v.skills, v.byKind)) {
      const box = el('div', `kp-prof kp-${r.prof}`, this.body);
      const top = el('div', 'kp-prof-top', box);
      el('span', 'kp-icon', top, PROF_ICON[r.prof]);
      el('span', 'kp-name', top, r.name);
      el('span', 'kp-skill', top, `${r.skill} / ${r.cap}`);
      const bar = el('div', 'kp-bar', box);
      const fill = el('div', 'kp-bar-fill', bar);
      fill.style.width = `${Math.round(r.pct * 100)}%`;
      el('div', 'kp-next', box, r.next);
      const tiers = el('div', 'kp-tiers', box);
      for (const t of r.tiers) {
        const row = el('div', `kp-tier${t.color === 'red' ? ' locked' : ''}`, tiers);
        const dot = el('span', 'kp-dot', row);
        dot.style.background = t.css;
        el('span', 'kp-tier-name', row, t.name);
        const req = el('span', 'kp-tier-req', row, t.color === 'red' ? `Requires ${t.req}` : t.gathered ? `${t.gathered} gathered` : `from ${t.req}`);
        req.style.color = t.color === 'red' ? '#ff7a7a' : '';
        row.title = `Tier ${t.tier}: needs ${r.name} ${t.req}. Colour = skill-up odds (orange always, yellow likely, green rarely, grey never).`;
      }
      const track = el('button', `kp-track${v.tracking === r.prof ? ' on' : ''}`, box, v.tracking === r.prof ? `Tracking ${r.name.toLowerCase()} nodes` : `Track ${r.name.toLowerCase()} nodes`);
      track.type = 'button';
      track.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        profView.actions?.setTracking(profView.tracking === r.prof ? null : r.prof);
      });
    }
    const auto = el('div', 'kp-auto', this.body);
    el('div', 'kp-auto-title', auto, 'Auto (Z) does');
    const seg = el('div', 'kp-seg', auto);
    for (const m of AUTO_MODES) {
      const b = el('button', `kp-seg-btn${v.autoMode === m ? ' on' : ''}`, seg, AUTO_MODE_LABEL[m]);
      b.type = 'button';
      b.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        profView.actions?.setAutoMode(m);
      });
    }
    el('div', 'kp-auto-help', auto, v.autoMode === 'hunt' ? 'Hunts monsters only.' : v.autoMode === 'gather' ? 'Gathers nodes it can work, fights only what attacks it.' : 'Hunts, and gathers nodes it passes.');
    el('div', 'kp-foot', this.body, `${v.gathers} gathers. Walk up to a glinting herb or ore and press T (or right-click it).`);
  }

  dispose(): void {
    this.root.remove();
  }
}

// ---------------------------------------------------------------------------

class NodeTip implements HudWidget {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly sub: HTMLElement;
  private readonly req: HTMLElement;
  private readonly flavor: HTMLElement;
  private readonly yields: HTMLElement;
  private readonly state: HTMLElement;
  private v = -1;

  constructor(host: HTMLElement) {
    this.root = el('div', 'kp-tip hk-hidden', host);
    this.title = el('div', 'kp-tip-title', this.root);
    this.sub = el('div', 'kp-tip-sub', this.root);
    this.req = el('div', 'kp-tip-req', this.root);
    this.yields = el('div', 'kp-tip-yields', this.root);
    this.flavor = el('div', 'kp-tip-flavor', this.root);
    this.state = el('div', 'kp-tip-state', this.root);
  }

  update(): void {
    const v = profView;
    if (v.hoverVersion === this.v) return;
    this.v = v.hoverVersion;
    const h = v.active ? v.hover : null;
    const node = h ? NODE_BY_ID.get(h.nodeId) : undefined;
    this.root.classList.toggle('hk-hidden', !node);
    if (!h || !node) return;
    const t = nodeTooltip(node, v.skills[node.profession], h.backInMs, h.dist);
    setText(this.title, t.title);
    this.title.style.color = t.req.color;
    setText(this.sub, t.sub);
    setText(this.req, t.req.text);
    this.req.style.color = t.req.color;
    setText(this.yields, t.yields.length ? `May yield: ${t.yields.join(', ')}` : 'Sells for a few coins');
    setText(this.flavor, t.flavor);
    setText(this.state, t.state.text);
    this.state.style.color = t.state.color;
    const w = 260;
    const x = Math.min(window.innerWidth - w - 8, h.x + 18);
    const y = Math.min(window.innerHeight - 170, h.y + 18);
    this.root.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }

  dispose(): void {
    this.root.remove();
  }
}

// ---------------------------------------------------------------------------

class ChannelBar implements HudWidget {
  private readonly root: HTMLElement;
  private readonly label: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly toasts: HTMLElement;
  private v = -1;

  constructor(host: HTMLElement) {
    this.root = el('div', 'kp-channel hk-hidden', host);
    this.label = el('div', 'kp-channel-label', this.root);
    const track = el('div', 'kp-channel-track', this.root);
    this.fill = el('div', 'kp-channel-fill', track);
    this.toasts = el('div', 'kp-toasts', host);
  }

  update(_h: unknown, realNow: number): void {
    const v = profView;
    if (v.channelVersion !== this.v) {
      this.v = v.channelVersion;
      const c = v.active ? v.channel : null;
      this.root.classList.toggle('hk-hidden', !c);
      if (c) {
        setText(this.label, `${c.profession === 'mining' ? 'Mining' : 'Gathering'} ${c.name}…`);
        this.root.dataset['prof'] = c.profession;
      }
    }
    const c = v.channel;
    if (c) this.fill.style.width = `${Math.round(Math.max(0, Math.min(1, (performance.now() - c.at) / c.durationMs)) * 100)}%`;
    while (v.toasts.length) {
      const t = v.toasts.shift()!;
      const box = el('div', `kp-toast kp-toast-${t.kind}`, this.toasts);
      el('div', 'kp-toast-title', box, t.title);
      if (t.text) el('div', 'kp-toast-text', box, t.text);
      const life = t.kind === 'tip' ? 9000 : 3600;
      setTimeout(() => box.classList.add('out'), life - 400);
      setTimeout(() => box.remove(), life);
    }
    void realNow;
  }

  dispose(): void {
    this.root.remove();
    this.toasts.remove();
  }
}

// ---------------------------------------------------------------------------

class Markers implements HudWidget {
  private readonly root: HTMLElement;
  private readonly pool: HTMLElement[] = [];

  constructor(host: HTMLElement) {
    this.root = el('div', 'kp-markers', host);
  }

  update(): void {
    const v = profView;
    const list = v.active && v.tracking && v.markers ? v.markers() : [];
    while (this.pool.length < list.length) this.pool.push(el('div', 'kp-marker', this.root));
    this.pool.forEach((m, i) => {
      const k = list[i];
      if (!k) {
        if (m.style.display !== 'none') m.style.display = 'none';
        return;
      }
      m.style.display = '';
      m.classList.toggle('edge', k.edge);
      m.style.setProperty('--c', k.color);
      m.style.transform = `translate(${Math.round(k.x)}px, ${Math.round(k.y)}px)${k.edge ? ` rotate(${k.angle}rad)` : ''}`;
      m.title = `${k.name} · ${Math.round(k.dist)} yd`;
    });
  }

  dispose(): void {
    this.root.remove();
  }
}

// ---------------------------------------------------------------------------

class AutoChip implements HudWidget {
  private readonly chip: HTMLButtonElement;
  private v = -1;

  constructor(host: HTMLElement, root: HTMLElement) {
    const auto = root.querySelector('.fx-auto') as HTMLElement | null;
    this.chip = el('button', 'kp-auto-chip hk-hit', auto ?? host);
    this.chip.type = 'button';
    this.chip.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const i = AUTO_MODES.indexOf(profView.autoMode);
      profView.actions?.setAutoMode(AUTO_MODES[(i + 1) % AUTO_MODES.length]!);
    });
  }

  update(): void {
    if (profView.settingsVersion === this.v) return;
    this.v = profView.settingsVersion;
    setText(this.chip, AUTO_MODE_LABEL[profView.autoMode]);
    this.chip.dataset['mode'] = profView.autoMode;
    this.chip.classList.toggle('hk-hidden', profView.autoMode === 'hunt'); // only shown for Gather / Both
    this.chip.title = `Auto setting: ${AUTO_MODE_LABEL[profView.autoMode]} (click to change: Hunt / Gather / Both)`;
  }

  dispose(): void {
    this.chip.remove();
  }
}

const screen = (make: (root: HTMLElement) => HudWidget) => (host: HTMLElement, ctx: { root: HTMLElement }) => {
  host.remove();
  return make(ctx.root);
};

export const PROF_WIDGETS: HudWidgetDef[] = [
  { id: 'profMarkers', region: 'bottomCenter', create: screen((r) => new Markers(r)) },
  { id: 'profChannel', region: 'bottomCenter', create: screen((r) => new ChannelBar(r)) },
  { id: 'profPanel', region: 'bottomCenter', create: screen((r) => new ProfPanel(r)) },
  { id: 'profTooltip', region: 'bottomCenter', create: screen((r) => new NodeTip(r)) },
  { id: 'profAuto', region: 'bottomCenter', create: (host, ctx) => new AutoChip(host, ctx.root) },
];

export { PROF_TINT, PROFESSION_NAME };
